import {
    AfterViewInit,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild
} from '@angular/core';
import { EntityCollectionService, EntityServices } from '@ngrx/data';
import { select, Store } from '@ngrx/store';
import { StateService } from '@uirouter/core';
import clone from 'just-clone';
import moment from 'moment';
import { combineLatest, merge, Observable, ReplaySubject } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    skip,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import * as fromDataServices from '../../data-services';
import * as fromGroups from '../../groups';
import { LoadGroupMembers } from '../../groups/store';
import * as fromLookups from '../../lookups';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    ProcessEnum,
    ResponseStatus
} from '../../shared';
import {
    DialogService,
    DynamicDialogRef,
    SelectItem
} from '../../shared/efaact-primeng.module';
import { UnsavedChangesService } from '../../shared/modal-dialog';
import { LoggingService } from '../../shared/services/logging/logging.service';
import * as fromUser from '../../user-info';
import { UserRoleEnum } from '../../user-info';
import { ReconciliationMemorizeTemplatePopupComponent } from '../reconciliation-memorize-template-popup/reconciliation-memorize-template-popup.component';
import { ReconciliationShareTemplatePopupComponent } from '../reconciliation-share-template-popup/reconciliation-share-template-popup.component';
// import * as fromScheduledDates from '../../lookups/store/schedule-date.selectors';

@Component({
    selector: 'ef-reconciliation-container',
    templateUrl: './reconciliation-container.component.html',
    styleUrls: ['./reconciliation-container.component.scss'],
    standalone: false
})
export class ReconciliationContainerComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    reconciliationService: EntityCollectionService<any>;
    reconciliationFieldsService: EntityCollectionService<fromDataServices.Reconciliation.Fields.ReconciliationFieldModel>;
    reconciliationTemplateService: EntityCollectionService<fromDataServices.Reconciliation.Template.ReconciliationTemplateModel>;
    fields$: Observable<
        fromDataServices.Reconciliation.Fields.ReconciliationFieldModel[]
    >;
    templates$: Observable<SelectItem[]>;
    users$: Observable<SelectItem[]>;
    userInfo$: Observable<fromUser.IUserInfo>;
    fieldsSelected$: Observable<
        fromDataServices.Reconciliation.Fields.ReconciliationFieldModel[]
    >;
    process$: Observable<fromShared.Process>;
    payPeriodDates$: Observable<fromLookups.ScheduledDate[]>;
    payPeriodDatesSelectItems$: Observable<SelectItem[]>;
    ShareTemplateSubject = new ReplaySubject<string>(1);
    PayPeriodSelectedSubject = new ReplaySubject<string>(1);
    DateSelectedSubject = new ReplaySubject<Date[]>(1);
    GroupSelectedSubject = new ReplaySubject(1);
    TemplateSelectedSubject = new ReplaySubject<string>(1);
    DownloadSubject = new ReplaySubject(1);
    RebuildSubject = new ReplaySubject(1);
    GetReconDataSubject = new ReplaySubject<{
        dates: Date[];
        process: fromShared.Process;
        rebuild: boolean;
    }>(1);
    FieldSelectedSubject =
        new ReplaySubject<fromDataServices.Reconciliation.Fields.ReconciliationFieldModel>(
            1
        );
    FieldReorderSubject = new ReplaySubject<
        fromDataServices.Reconciliation.Fields.ReconciliationFieldModel[]
    >(1);
    visible = true;
    private active = true;
    private dialogRef: DynamicDialogRef;
    showFilterDates = false;
    showPayPeriods = false;
    @ViewChild('divExport', { static: true }) divExport: ElementRef;
    constructor(
        private entityServices: EntityServices,
        private dialogService: DialogService,
        private store: Store,
        private log: LoggingService,
        private ss: StateService,
        private renderer: Renderer2,
        private confirmService: UnsavedChangesService
    ) {
        this.reconciliationService =
            this.entityServices.getEntityCollectionService(
                fromDataServices.Reconciliation.entityName
            );
        this.reconciliationFieldsService =
            this.entityServices.getEntityCollectionService<fromDataServices.Reconciliation.Fields.ReconciliationFieldModel>(
                fromDataServices.Reconciliation.Fields.entityName
            );
        this.reconciliationTemplateService =
            this.entityServices.getEntityCollectionService<fromDataServices.Reconciliation.Template.ReconciliationTemplateModel>(
                fromDataServices.Reconciliation.Template.entityName
            );

        this.log.verbose({ type: 'ReconciliationContainerComponent::' });
    }
    ngOnInit(): void {
        this.log.verbose({ type: 'ReconciliationContainerComponent:ngOnInit' });
        this.reconciliationTemplateService.clearCache();
        this.reconciliationTemplateService.getAll();
        this.reconciliationService.clearCache();
        this.store.dispatch(
            new LoadGroupMembers({ GroupID: UserRoleEnum.Administrator })
        );

        this.setupObservables();
        this.setupSubscriptions();
    }
    ngOnDestroy(): void {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:ngOnDestroy'
        });
        this.active = false;
        if (this.dialogRef) {
            this.dialogRef.close();
            // this.dialogRef.destroy();
        }
    }
    ngAfterViewInit(): void {
        setTimeout(() => {
            this.handleOnOpen([1]);
        }, 0);
    }
    setupObservables(): void {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:setupObservables'
        });

        this.users$ = this.store.pipe(
            select(fromGroups.getGroupMembers),
            // map(items => items.filter(d => d.IsActive)),
            // filter(x => !!x && x.length > 0),
            tap(admins =>
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:users$',
                    admins
                })
            ),
            map(items =>
                items
                    .filter(item => item.IsGroupMember)
                    .map(item => ({
                        label: item.UserName,
                        value: item.ListID
                    }))
            )
        );
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.userInfo$ = this.store.pipe(select(fromUser.getUserInfo));
        this.templates$ = this.reconciliationTemplateService.entities$.pipe(
            map(items =>
                items.map(item => ({ label: item.Name, value: item.Name }))
            )
        );
        this.fields$ = this.reconciliationFieldsService.entities$.pipe(
            map(items => clone(items))
        );
        this.fieldsSelected$ = merge(
            this.FieldReorderSubject,
            this.fields$
        ).pipe(
            // withLatestFrom(this.fields$),
            map(fields => fields.filter(field => field.InUse)),
            tap(fields =>
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:fieldsSelected$',
                    fields
                })
            )
        );
        this.payPeriodDates$ = this.store.pipe(
            select(
                fromLookups.ScheduledDateSelectors.selectScheduledDateType({
                    ScheduledDateType: 'PPE'
                })
            )
        );
        this.payPeriodDatesSelectItems$ = this.payPeriodDates$.pipe(
            map(dates =>
                dates.map(d => ({
                    label: `${moment(d.ScheduledStartDate).format(
                        'MM/DD/YY'
                    )} - ${moment(d.ScheduledEndDate).format('MM/DD/YY')} ${
                        d.ScheduledDatePurpose
                    }`,
                    value: d.ScheduledDateID
                }))
            )
        );
    }
    setupSubscriptions(): void {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:setupSubscriptions'
        });
        this.userInfo$.pipe(take(1)).subscribe(user => {
            this.store.dispatch(
                new fromLookups.LoadDirectReports({
                    user,
                    activeOnly: false
                })
            );
        });
        this.userInfo$
            .pipe(
                takeWhile(() => this.active),
                skip(1)
            )
            .subscribe(user => {
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:userInfo$',
                    user
                });
                this.store.dispatch(
                    new fromLookups.LoadDirectReports({
                        user,
                        activeOnly: false
                    })
                );
                this.DateSelectedSubject.next([
                    user.PrevPayPeriodStart,
                    user.PrevPayPeriodEnd
                ]);
                this.RebuildSubject.next();
            });
        this.ShareTemplateSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.users$),
            withLatestFrom(this.userInfo$),
            withLatestFrom(this.process$)
        ).subscribe(([[[template, users], ui], process]) =>
            this.shareTemplate(
                template,
                users.filter(x => x.value !== ui.EmployeeListID),
                process
            )
        );
        this.GroupSelectedSubject.pipe(takeWhile(() => this.active)).subscribe(
            () => {
                this.reconciliationService.clearCache();
                $('#btnRebuild').addClass('blinking');
            }
        );
        combineLatest([this.userInfo$, this.FieldSelectedSubject])
            .pipe(takeWhile(() => this.active))
            .subscribe(([user, field]) => {
                this.reconciliationFieldsService.upsert({
                    ...field,
                    PersonListID: user.EmployeeListID
                });
            });
        this.process$.pipe(takeWhile(() => this.active)).subscribe(process => {
            this.reconciliationFieldsService.getWithQuery({
                Processid: process.ProcessID.toString(),
                PersonListID: ''
            });
            switch (process.ProcessID) {
                case ProcessEnum.TimeLaborReconciliation:
                    this.showPayPeriods = true;
                    this.showFilterDates = false;
                    break;

                default:
                    this.showPayPeriods = false;
                    this.showFilterDates = false;
                    break;
            }
        });
        this.DateSelectedSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.process$),
            tap(([dates, process]) =>
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:DateSelectedSubject,process$',
                    dates,
                    process
                })
            )
        ).subscribe(([dates, process]) =>
            this.getReconData(dates, process, false)
        );
        this.RebuildSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.DateSelectedSubject),
            withLatestFrom(this.FieldSelectedSubject),
            withLatestFrom(this.process$),
            tap(([[[, dates], fields], process]) =>
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:RebuildSubject,DateSelectedSubject,FieldSelectedSubject,process$',
                    dates,
                    fields,
                    process
                })
            )
        ).subscribe(([[[, dates], fields], process]) => {
            this.getReconData(dates, process, true);
        });
        combineLatest([this.PayPeriodSelectedSubject, this.payPeriodDates$])
            .pipe(
                takeWhile(() => this.active),
                tap(([selected, payPeriods]) =>
                    this.log.verbose({
                        type: 'ReconciliationContainerComponent:PayPeriodSelectedSubject,payPeriodDates$',
                        selected,
                        payPeriods
                    })
                )
            )
            .subscribe(([selected, payPeriods]) => {
                const period = payPeriods.find(
                    x => x.ScheduledDateID === selected
                );
                if (period) {
                    this.DateSelectedSubject.next([
                        period.ScheduledStartDate,
                        period.ScheduledEndDate
                    ]);
                }
            });
        this.GetReconDataSubject.pipe(
            takeWhile(() => this.active),
            tap(x =>
                this.log.verbose({
                    type: 'ReconciliationContainerComponent:getReconData',
                    x
                })
            ),
            distinctUntilChanged(
                (
                    x: {
                        dates: Date[];
                        process: fromShared.Process;
                        rebuild: boolean;
                    },
                    y: {
                        dates: Date[];
                        process: fromShared.Process;
                        rebuild: boolean;
                    }
                ) => {
                    const result =
                        x.dates[0].valueOf() === y.dates[0].valueOf() &&
                        x.dates[1].valueOf() === y.dates[1].valueOf() &&
                        x.process.ProcessID === y.process.ProcessID &&
                        !y.rebuild;
                    this.log.verbose({
                        type: 'ReconciliationContainerComponent:getReconData:distinctUntilChanged',
                        x,
                        y,
                        result
                    });
                    return result;
                }
            )
        ).subscribe(({ dates, process }) => {
            this.log.verbose({
                type: 'ReconciliationContainerComponent:getReconData',
                dates,
                process
            });
            this.reconciliationService.clearCache();
            this.reconciliationService.getWithQuery({
                StartDate: moment(dates[0]).format('MM/DD/YYYY'),
                EndDate: moment(dates[1]).format('MM/DD/YYYY'),
                ProcessID: process.ProcessID.toString()
            });
        });
    }
    onRebuildClicked() {
        this.RebuildSubject.next();
        $('#btnRebuild').removeClass('blinking');
    }
    onDateRangeSelected($event: any) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onDateRangeSelected',
            $event
        });
        if (this.showFilterDates) {
            this.DateSelectedSubject.next($event);
        }
        if (this.showPayPeriods) {
            this.PayPeriodSelectedSubject.next($event);
        }
    }
    onTemplateSelected($event) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onTemplateSelected',
            $event
        });
        this.TemplateSelectedSubject.next($event);
    }
    onMemorize($event) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onMemorize',
            $event
        });
        const ref = this.dialogService.open(
            ReconciliationMemorizeTemplatePopupComponent,
            {
                header: 'Memorize Template',
                closable: false,
                closeOnEscape: false,
                showHeader: true,
                modal: true,
                data: {
                    // provide array of already existing template names
                    names: ['test']
                }
            }
        );
        ref.onClose.pipe(take(1)).subscribe(data => {
            this.log.verbose({
                type: 'ReconciliationContainerComponent:onMemorize:afterClosed',
                data
            });
        });
    }
    onShareTemplate($event) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onShareTemplate',
            $event
        });
        this.ShareTemplateSubject.next($event);
    }
    shareTemplate(template, users, process) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:ShareTemplateSubject,users$',
            template,
            users,
            process
        });

        this.dialogRef = this.dialogService.open(
            ReconciliationShareTemplatePopupComponent,
            {
                header: 'Share Template',
                closable: false,
                closeOnEscape: false,
                showHeader: true,
                modal: true,
                data: {
                    template,
                    items: users,
                    process
                }
            }
        );
        this.dialogRef.onClose.pipe(take(1)).subscribe(data => {
            this.dialogRef = null;
            this.log.verbose({
                type: 'ReconciliationContainerComponent:onShareTemplate:afterClosed',
                data
            });
            if (data && data.Users) {
                this.reconciliationFieldsService
                    .upsert({
                        id: -1,
                        ProcessID: process.ProcessID,
                        UserDirectReports: data.Users.map(u => ({
                            EmployeeListID: u,
                            EmployeeName: '',
                            IsActive: null
                        }))
                    } as any)
                    .pipe(takeWhile(() => this.active))
                    .pipe(filter(item => item.id === -1))
                    .subscribe(result => {
                        this.log.verbose({
                            type: 'ReconciliationContainerComponent:onShareTemplate:afterUpdate',
                            result
                        });
                        if (
                            !!result &&
                            result.Status.StatusCode < DisplayMessageMinimum
                        ) {
                            this.confirmService.confirm({
                                showCancel: false,
                                dialogText: 'Template share completed.',
                                maxStatus: ResponseStatus.Success
                            });
                        }
                    });
            }
        });
    }
    onFieldSelected(
        item: fromDataServices.Reconciliation.Fields.ReconciliationFieldModel
    ) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onFieldSelected',
            item
        });
        this.FieldSelectedSubject.next(item);
    }
    onRowReorder(
        items: fromDataServices.Reconciliation.Fields.ReconciliationFieldModel[]
    ) {
        this.log.verbose({
            type: 'ReconciliationContainerComponent:onRowReorder',
            items
        });
        this.FieldReorderSubject.next(items);
    }
    onGroupSelected() {
        this.GroupSelectedSubject.next();
    }
    getReconData(dates: Date[], process: fromShared.Process, rebuild: boolean) {
        this.GetReconDataSubject.next({ dates, process, rebuild });
    }
    handleOnOpen($event) {
        const baseHeight = 90;
        let accHeight = 0;
        if ($event === 0 || $event.includes(0)) {
            accHeight += 25;
        }
        if ($event === 1 || $event.includes(1)) {
            accHeight += 30;
        }
        // if ($event === 2 || $event.includes(2)) {
        //     accHeight += 35;
        // }
        this.renderer.setStyle(
            this.divExport.nativeElement,
            'height',
            `${baseHeight - accHeight}vh`
        );
        // this.cdRef.detach();
        // this.log.verbose({
        //     type: 'ReconciliationContainerComponent:handleOnOpen',
        //     height: this.divExport.nativeElement.style.height
        // });
        this.visible = false;
        setTimeout(() => {
            this.log.verbose({
                type: 'ReconciliationContainerComponent:handleOnOpen'
            });
            this.visible = true;
            // this.cdRef.reattach();
            // this.cdRef.detectChanges();
        }, 0);
    }
}
